import type { HrsnDomain } from '@/types/api';

/**
 * S2 — qué escribir en la caja de Notas de cada dominio.
 *
 * El reporte: en la pregunta de discapacidad la screener agrega información
 * que no corresponde a la pregunta.
 *
 * Diagnóstico (DISENO-scr2-cola.md §S2): la pregunta de discapacidad es el
 * dominio `physical_ability` y NO tiene campo de texto — son dos radios, y el
 * server rechaza cualquier otra forma de respuesta. O sea: el texto que sobra
 * no puede estar en la respuesta, está en la caja de Notas, que es genérica
 * para los nueve dominios, invita explícitamente a «free-form notes» y tiene
 * 4000 caracteres de espacio. La screener está usando la caja para lo que la
 * caja pide. Es wording, no una validación faltante.
 *
 * Lo que NO se hace, y es deliberado: ninguna validación dura sobre el
 * contenido. Es PHI escrito por un humano durante una llamada; bloquear un
 * guardado pierde información clínica que no se recupera. Y bajar el
 * `@MaxLength(4000)` del DTO truncaría notas históricas legítimas en la
 * próxima edición.
 *
 * Fallback a propósito: sólo los dominios con una guía escrita la tienen. Los
 * otros ocho caen al texto genérico en vez de inflar el cambio con dieciocho
 * textos nuevos que nadie revisó.
 */

export type Lang = 'en' | 'es';

export const NOTES_MAX_LENGTH = 4000;

/**
 * Umbral blando del aviso, en caracteres. Es un dominio de dos radios: si la
 * nota pasa de esto, casi seguro está contando otra cosa.
 *
 * NUNCA bloquea: es un aviso y el guardado sigue igual.
 */
export const NOTES_SOFT_HINT_CHARS = 300;

const PLACEHOLDERS: Partial<Record<HrsnDomain, { en: string; es: string }>> = {
  physical_ability: {
    en:
      'Only what answers this question: difficulty walking or climbing stairs, dressing or bathing. ' +
      'Anything else belongs in its own domain.',
    es:
      'Sólo lo que responde a esta pregunta: dificultad para caminar o subir escaleras, vestirse o bañarse. ' +
      'Otros temas van en el dominio que les corresponde.',
  },
};

const GENERIC = {
  en: 'Free-form notes for this domain. Auto-saved with the answer.',
  es: 'Notas para este dominio. Se guardan automáticamente.',
};

/** El placeholder de la caja de Notas de ese dominio, en ese idioma. */
export function notesPlaceholderFor(domain: HrsnDomain, lang: Lang): string {
  const specific = PLACEHOLDERS[domain];
  return specific ? specific[lang] : GENERIC[lang];
}

/** Los dominios que tienen guía propia escrita (el resto usa la genérica). */
export function domainsWithOwnNotesPlaceholder(): HrsnDomain[] {
  return Object.keys(PLACEHOLDERS) as HrsnDomain[];
}

/**
 * ¿Mostrar el aviso blando? Sólo cuenta caracteres; no mira el contenido.
 *
 * Mirar el contenido (regex de palabras de otros dominios) sería la tentación
 * obvia y es justo lo que el diseño descarta: adivinar mal sobre PHI y
 * regañar a la screener en medio de una llamada.
 */
export function shouldShowNotesHint(notes: string): boolean {
  return notes.length > NOTES_SOFT_HINT_CHARS;
}
